import { Path } from "app/constants/paths";
import { useAuth } from "app/hooks";
import { useTheme } from "app/shared";
import HeaderConnect from "app/widgets/crypto-header/header-connect";
import { FC, useEffect, useState, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  useCreateApplicationMutation,
  useSubscribeToWhitelistMutation,
} from "store/api/new.api";
import { useAccount } from "wagmi";
import Button from "../../button/button";
import styles from "./timer.module.scss";

interface RequirementsCountdownWidgetProps {
  whitelistStartDate: Date;
  whitelistEndDate: Date;
  sellStartDate: Date;
  sellEndDate: Date;
  brevoId?: number;
  totalTokens?: number;
  totalBalance?: number;
}

const RequirementsWidget: FC<RequirementsCountdownWidgetProps> = ({
  whitelistStartDate,
  whitelistEndDate,
  sellStartDate,
  brevoId,
  sellEndDate,
  totalTokens,
  totalBalance,
}) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [timerText, setTimerText] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isModalOpen, setModalOpen] = useState(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const { theme } = useTheme();
  const [createApplication, { isLoading: isCreating }] =
    useCreateApplicationMutation();
  const { address } = useAccount();
  const isAuth = useAuth();
  const history = useHistory();

  const { idoId } = useParams<{ idoId: string }>();

  const [subscribeToWhitelist, { isLoading: isSubscribeLoading }] =
    useSubscribeToWhitelistMutation();

  const handleModalSubmit = async (formData: {
    email: string;
    allocation: string;
  }) => {
    if (!address || !idoId) return;

    try {
      await createApplication({
        idoId,
        address,
        email: formData.email,
        wantedAllocation: formData.allocation,
      }).unwrap();

      await subscribeToWhitelist({
        email: formData.email,
        listId: brevoId!,
      }).unwrap();

      setHasError(false);
    } catch (error) {
      console.error("Failed to create application:", error);
      setHasError(true);
    }
  };

  const calculateTimeLeft = useCallback(() => {
    // Check if all required dates are valid
    if (
      !whitelistStartDate ||
      !whitelistEndDate ||
      !sellStartDate ||
      !sellEndDate
    ) {
      return;
    }

    const currentTime = new Date();
    let targetDate: Date;
    let newTimerText: string;
    let newIsButtonDisabled: boolean;

    if (currentTime < whitelistStartDate) {
      targetDate = whitelistStartDate;
      newTimerText = "SmartWhitelist Start In:";
      newIsButtonDisabled = true;
    } else if (
      currentTime >= whitelistStartDate &&
      currentTime < whitelistEndDate
    ) {
      targetDate = whitelistEndDate;
      newTimerText = "SmartWhitelist Ends In:";
      newIsButtonDisabled = false;
    } else if (currentTime < sellStartDate) {
      targetDate = sellStartDate;
      newTimerText = "IDO Start In:";
      newIsButtonDisabled = false;
    } else if (currentTime >= sellStartDate && currentTime < sellEndDate) {
      targetDate = sellEndDate;
      newTimerText = "IDO Time Left:";
      newIsButtonDisabled = false;
    } else if (totalTokens && totalBalance && totalTokens <= totalBalance) {
      targetDate = new Date();
      newTimerText = "Sold Out";
      newIsButtonDisabled = true;
    } else {
      targetDate = new Date();
      newTimerText = "Sold Out";
      newIsButtonDisabled = true;
    }

    const difference = targetDate.getTime() - currentTime.getTime();

    // Update states in a batch to prevent multiple re-renders
    setTimerText((prevText) =>
      prevText !== newTimerText ? newTimerText : prevText
    );
    setIsButtonDisabled((prevDisabled) =>
      prevDisabled !== newIsButtonDisabled ? newIsButtonDisabled : prevDisabled
    );

    if (difference > 0) {
      const newTimeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
      setTimeLeft((prevTimeLeft) => {
        if (
          prevTimeLeft.days !== newTimeLeft.days ||
          prevTimeLeft.hours !== newTimeLeft.hours ||
          prevTimeLeft.minutes !== newTimeLeft.minutes ||
          prevTimeLeft.seconds !== newTimeLeft.seconds
        ) {
          return newTimeLeft;
        }
        return prevTimeLeft;
      });
    } else {
      setTimeLeft((prevTimeLeft) => {
        if (
          prevTimeLeft.days !== 0 ||
          prevTimeLeft.hours !== 0 ||
          prevTimeLeft.minutes !== 0 ||
          prevTimeLeft.seconds !== 0
        ) {
          return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
        return prevTimeLeft;
      });
    }
  }, [
    whitelistStartDate,
    whitelistEndDate,
    sellStartDate,
    sellEndDate,
    totalTokens,
    totalBalance,
  ]);

  useEffect(() => {
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  const handleApplyToWhitelist = () => {
    setModalOpen(true);
  };

  return (
    <div
      className={`${styles.widget} ${theme === "dark" ? styles.darkTheme : ""}`}
    >
      <div className={styles.wrapper}>
        <div>
          <div
            className={`${
              timerText !== "Sold Out" ? styles.timer : styles.soldOut
            }`}
          >
            <h3 className={styles.title}>{timerText}</h3>
            <div className={styles.time}>
              <div>
                <span className={styles.time1}>
                  {String(timeLeft.days).padStart(2, "0")}
                </span>
                <span
                  className={`${styles.time2} ${
                    theme === "dark" ? styles.darkTheme : ""
                  }`}
                >
                  DAYS
                </span>
              </div>
              <div>
                <span className={styles.time1}>
                  {String(timeLeft.hours).padStart(2, "0")}
                </span>
                <span
                  className={`${styles.time2} ${
                    theme === "dark" ? styles.darkTheme : ""
                  }`}
                >
                  HOURS
                </span>
              </div>
              <div>
                <span className={styles.time1}>
                  {String(timeLeft.minutes).padStart(2, "0")}
                </span>
                <span
                  className={`${styles.time2} ${
                    theme === "dark" ? styles.darkTheme : ""
                  }`}
                >
                  MINUTES
                </span>
              </div>
              <div>
                <span className={styles.time1}>
                  {String(timeLeft.seconds).padStart(2, "0")}
                </span>
                <span
                  className={`${styles.time2} ${
                    theme === "dark" ? styles.darkTheme : ""
                  }`}
                >
                  SECONDS
                </span>
              </div>
            </div>
            {timerText === "Sold Out" && (
              <div className={styles.claimBtn}>
                <Button onClick={() => history.push(Path.CLAIM)}>
                  Claim Details
                </Button>
              </div>
            )}
          </div>
          {!isAuth && (
            <div className={styles.connector}>
              <HeaderConnect />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequirementsWidget;
